import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetCurrentUserQuery,
  useGetCurrentUserProfileQuery,
  useUpdateCurrentUserProfileMutation,
  useUpdateUserAutoPurchaseMutation,
} from '@app/store/slices/users/usersApi';
import { useDateOfBirthInput } from './useDateOfBirthInput';
import { useApiErrorHandler } from './useApiErrorHandler';
import { useDisplayError } from './useDisplayError';
import { formatBirthday, parseBirthday } from '@app/shared/utils/birthday';
import { prepareProfileUpdatePayload, sanitizeProfileForm } from '@app/shared/utils/userProfile';
import notification from '@app/shared/utils/notification';
import { CurrentUserProfileFormState } from '@/app/store/slices/users/usersTypes';

export function useUserProfileForm() {
  const [hasFormError, setHasFormError] = useState(false);
  const { data: user, isError: isUserError } = useGetCurrentUserQuery();
  const { data: userProfile, isError: isUserProfileError } = useGetCurrentUserProfileQuery();
  const [updateCurrentUserProfile, { isLoading: isUpdatingCurrentUserProfile }] =
    useUpdateCurrentUserProfileMutation();
  const [updateUserAutoPurchase, { isLoading: isUpdatingUserAutoPurchase }] =
    useUpdateUserAutoPurchaseMutation();
  const {
    dateOfBirth,
    setDateOfBirth,
    dateOfBirthErrorTranslationKey,
    handleDateOfBirthChange,
    handleDateOfBirthClear,
    validateDateOfBirth,
  } = useDateOfBirthInput();
  const { handleFetchError } = useApiErrorHandler();
  const { t } = useTranslation();

  const [formState, setFormState] = useState(sanitizeProfileForm(user, userProfile));

  useDisplayError(isUserError || isUserProfileError, t('errors.failed_to_load_user'));

  useEffect(() => {
    if (!user || !userProfile) {
      return;
    }

    if (userProfile.birthday) {
      const parsedBirthday = parseBirthday(userProfile.birthday);
      setDateOfBirth(parsedBirthday);
    }
  }, [user, userProfile]);

  const handleFormStateChange = <K extends keyof CurrentUserProfileFormState>(
    field: K,
    value: CurrentUserProfileFormState[K]
  ) => {
    if (hasFormError) {
      setHasFormError(false);
    }
    setFormState((prevState) => ({ ...prevState, [field]: value }));
  };

  const handleFormSubmit = async () => {
    setHasFormError(false);

    if (!validateDateOfBirth() || !user || !userProfile) {
      setHasFormError(true);
      return;
    }

    try {
      const updatePayload = prepareProfileUpdatePayload(formState);
      const { day, month, year } = dateOfBirth;
      const formattedBirthday = formatBirthday(day, month, year);

      const updatePromise = [];
      if (formState.autoPurchase !== user.autoPurchase) {
        updatePromise.push(
          updateUserAutoPurchase({ autoPurchase: formState.autoPurchase }).unwrap()
        );
      }
      updatePromise.push(
        updateCurrentUserProfile({ ...updatePayload, birthday: formattedBirthday }).unwrap()
      );

      await Promise.all(updatePromise);

      if (formState.autoPurchase !== user.autoPurchase) {
        if (formState.autoPurchase) {
          notification.success(t('edit_profile.auto_purchase'));
        } else {
          notification.warning(t('edit_profile.auto_purchase_not_enabled'));
        }
      } else {
        notification.success(t('edit_profile.success'));
      }
    } catch (err: unknown) {
      handleFetchError(err);
    }
  };

  return {
    isUpdatingCurrentUserProfile: isUpdatingCurrentUserProfile || isUpdatingUserAutoPurchase,
    dateOfBirth,
    dateOfBirthErrorTranslationKey,
    handleDateOfBirthChange,
    handleDateOfBirthClear,
    formState,
    handleFormStateChange,
    handleFormSubmit,
    hasFormError,
  };
}
